import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import {HashMap, TranslocoPipe} from '@jsverse/transloco';
import {ButtonModule} from 'primeng/button';
import {Tag} from 'primeng/tag';
import {DirectusTranslatedItem, DirectusTranslatedItemGroup} from '@frontend/directus';
import {AppNumberPipe} from '@frontend/shared';
import {
  ProjectReferenceCompanyVisibility,
  ProjectReferenceDocumentResponse,
  ProjectReferenceScoreView,
} from '@frontend/shared-api';
import {DeleteItemButtonComponent} from '../../../buttons/delete-item-button/delete-item-button.component';
import {AppPanelComponent} from '../../../header/panel/app-panel.component';
import {ModelFieldDatepickerComponent} from '../../../inputs/model-field/model-field-datepicker/model-field-datepicker.component';
import {ModelFieldMultiselectComponent} from '../../../inputs/model-field/model-field-multiselect/model-field-multiselect.component';
import {ModelFieldNumberComponent} from '../../../inputs/model-field/model-field-number/model-field-number.component';
import {ModelFieldSelectButtonComponent} from '../../../inputs/model-field/model-field-select-button/model-field-select-button.component';
import {ModelFieldSelectComponent} from '../../../inputs/model-field/model-field-select/model-field-select.component';
import {ModelFieldTextComponent} from '../../../inputs/model-field/model-field-text/model-field-text.component';
import {ModelFieldTextareaComponent} from '../../../inputs/model-field/model-field-textarea/model-field-textarea.component';
import {ConfirmStepLeaveDirective} from '../../../../project-step-save-button/confirm-step-leave.directive';
import {createLocalDate, isEndDateBeforeStartDate} from '../../../../utils/date-utils';
import {FunctionalScopePickerComponent} from '../functional-scope-picker/functional-scope-picker.component';
import {ProjectReferenceDocumentComponent} from '../project-reference-document/project-reference-document.component';
import {FunctionalScopeEntry, ProjectReference} from '../project-references.types';
import {ReferenceScoreBadgeComponent} from '../reference-score-badge/reference-score-badge.component';
import {ANONYMIZED_COMPANY_NAME, VISIBILITY_OPTIONS} from './project-reference-card.types';

@Component({
  selector: 'app-project-reference-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppNumberPipe,
    AppPanelComponent,
    ButtonModule,
    ConfirmStepLeaveDirective,
    DeleteItemButtonComponent,
    FunctionalScopePickerComponent,
    ModelFieldDatepickerComponent,
    ModelFieldMultiselectComponent,
    ModelFieldNumberComponent,
    ModelFieldSelectButtonComponent,
    ModelFieldSelectComponent,
    ModelFieldTextComponent,
    ModelFieldTextareaComponent,
    ProjectReferenceDocumentComponent,
    ReferenceScoreBadgeComponent,
    Tag,
    TranslocoPipe,
  ],
  templateUrl: './project-reference-card.component.html',
})
export class ProjectReferenceCardComponent {
  protected readonly VISIBILITY_OPTIONS = VISIBILITY_OPTIONS;

  readonly reference = input.required<ProjectReference>();
  readonly position = input.required<number>();
  readonly total = input.required<number>();
  readonly industryOptions = input.required<DirectusTranslatedItem[]>();
  readonly moduleGroups = input.required<DirectusTranslatedItemGroup[]>();
  readonly functionStatusOptions = input.required<DirectusTranslatedItem[]>();
  readonly countryOptions = input.required<DirectusTranslatedItem[]>();
  readonly languageOptions = input.required<DirectusTranslatedItem[]>();
  readonly readOnly = input<boolean>(false);
  readonly score = input<ProjectReferenceScoreView>();

  readonly save = output<ProjectReference>();
  readonly remove = output<ProjectReference>();
  readonly hasChangesChange = output<boolean>();

  protected companyName = linkedSignal<string | null>(() => this.reference().companyName);
  protected visibility = linkedSignal<ProjectReferenceCompanyVisibility>(
    () => this.reference().visibility,
  );
  protected projectName = linkedSignal<string | null>(() => this.reference().projectName);
  protected startDate = linkedSignal<Date | null>(() => this.reference().startDate);
  protected endDate = linkedSignal<Date | null>(() => this.reference().endDate);
  protected headcount = linkedSignal<number | null>(() => this.reference().headcount);
  protected industryId = linkedSignal<string | null>(() => this.reference().industryId);
  protected functionalScope = linkedSignal<FunctionalScopeEntry[]>(
    () => this.reference().functionalScope,
  );
  protected countryCodes = linkedSignal<string[]>(() => this.reference().countryCodes);
  protected languageCode = linkedSignal<string | null>(
    () => this.reference().languageCodes[0] ?? null,
  );
  protected document = linkedSignal<ProjectReferenceDocumentResponse | null>(
    () => this.reference().document,
  );
  protected documentSummary = linkedSignal<string | null | undefined>(
    () => this.reference().documentSummary,
  );
  private readonly savedDocumentSummary = linkedSignal<string>(
    () => this.reference().documentSummary,
  );
  protected readonly comment = linkedSignal<string | null | undefined>(
    () => this.reference().comment,
  );

  protected readonly titleParams = computed<HashMap<string | number>>(() => ({
    position: this.position(),
    total: this.total(),
    companyName:
      this.visibility() === ProjectReferenceCompanyVisibility.ANONYMIZED
        ? ANONYMIZED_COMPANY_NAME
        : (this.companyName() ?? ''),
    projectName: this.projectName() ?? '',
  }));

  private readonly editedReference = computed<ProjectReference>(() => ({
    localId: this.reference().localId,
    id: this.reference().id,
    companyName: this.companyName() ?? '',
    visibility: this.visibility(),
    projectName: this.projectName() ?? '',
    startDate: this.startDate(),
    endDate: this.endDate(),
    headcount: this.headcount(),
    industryId: this.industryId(),
    functionalScope: this.functionalScope(),
    countryCodes: this.countryCodes(),
    languageCodes: this.toLanguageCodes(),
    document: this.document(),
    documentSummary: this.documentSummary() ?? '',
    comment: this.comment() ?? '',
  }));

  protected readonly today = computed<Date>(() => createLocalDate());

  protected readonly isEndDateBeforeStart = computed<boolean>(() =>
    isEndDateBeforeStartDate(this.startDate(), this.endDate()),
  );

  protected readonly isValid = computed<boolean>(() => {
    const edited = this.editedReference();
    const today: Date = this.today();

    return (
      edited.companyName.trim() !== '' &&
      edited.projectName.trim() !== '' &&
      edited.startDate !== null &&
      edited.startDate.getTime() <= today.getTime() &&
      edited.endDate !== null &&
      !this.isEndDateBeforeStart() &&
      edited.headcount !== null &&
      edited.headcount > 0 &&
      edited.industryId !== null &&
      edited.functionalScope.length > 0 &&
      edited.countryCodes.length > 0 &&
      edited.languageCodes.length > 0 &&
      (edited.document === null || edited.documentSummary.trim() !== '')
    );
  });

  protected readonly hasChanges = computed<boolean>(() => {
    const edited = this.editedReference();
    const reference = this.reference();

    return (
      edited.companyName !== reference.companyName ||
      edited.visibility !== reference.visibility ||
      edited.projectName !== reference.projectName ||
      edited.startDate?.getTime() !== reference.startDate?.getTime() ||
      edited.endDate?.getTime() !== reference.endDate?.getTime() ||
      edited.headcount !== reference.headcount ||
      edited.industryId !== reference.industryId ||
      JSON.stringify(edited.functionalScope) !== JSON.stringify(reference.functionalScope) ||
      edited.countryCodes.join() !== reference.countryCodes.join() ||
      edited.languageCodes.join() !== reference.languageCodes.join() ||
      edited.documentSummary !== this.savedDocumentSummary() ||
      edited.comment !== reference.comment
    );
  });

  protected readonly isSaved = computed<boolean>(() => this.reference().id !== null);

  protected readonly referenceId = computed<number>(() => {
    const id: number | null = this.reference().id;

    if (id === null) {
      throw new Error('A saved project reference must have an id');
    }

    return id;
  });

  protected readonly isEditable = computed<boolean>(() => !this.readOnly());

  protected readonly canSave = computed<boolean>(
    () => this.isEditable() && this.isValid() && this.hasChanges(),
  );

  protected readonly scaScores = computed<ReadonlyMap<number, number>>(
    () =>
      new Map(
        (this.score()?.subCriteriaAttributeScores ?? []).map((current) => [
          current.scaNumber,
          current.score,
        ]),
      ),
  );

  constructor() {
    effect(() => {
      this.hasChangesChange.emit(this.hasChanges());
    });
  }

  protected onSave(): void {
    this.save.emit(this.editedReference());
  }

  protected onRemove(): void {
    this.remove.emit(this.reference());
  }

  protected onDocumentRemoved(): void {
    this.savedDocumentSummary.set('');
  }

  private toLanguageCodes(): string[] {
    const languageCode: string | null = this.languageCode();

    return languageCode === null ? [] : [languageCode];
  }
}
